import { useEffect, useState } from 'react';

import NutrientRow from '../../nutrition-label/components/NutrientRow';
import SeparatorBar from '../../nutrition-label/components/SeparatorBar';
import NutritionBreakdown from '../../nutrition-label/components/utils/NutritionBreakdown';

import { useGetBasicUomEnum } from 'hooks';
import { parseNumber } from 'utils/parser';
import { getUomTypesAbbreviations } from '../../nutrition-label/DailyValueConstants';
import { getNutritionNameByTypeCode } from '../../nutrition-label/constants';
import { renderContainerDescriptor } from '../components/utils';

import './VerticalLabel.less';

function VerticalLabel(props) {
  const { label } = props;

  const [nutrient, setNutrient] = useState([]);

  const { basicUomProperties } = useGetBasicUomEnum();

  useEffect(() => {
    async function getData() {
      const nb = await NutritionBreakdown(label?.nutritionFactProperties);
      setNutrient(nb);
    }
    if (label) {
      getData();
    }
  }, [label]);

  const { primaryNutrition, secondaryNutrition } = nutrient;

  const showAbbr = (quantityUom, defaultUom = 'g') => {
    return (
      getUomTypesAbbreviations(basicUomProperties, quantityUom) ?? defaultUom
    );
  };

  const servingPerContainerDescriptor = renderContainerDescriptor(
    label?.servingPerContainerDescriptor,
  );

  const getNutrientBasisQtyTypeCode = (label) => {
    if (label?.nutrientBasisQuantityTypeCode === 'By Child Nutrition serving') {
      return 'Child Nutrition serving';
    }
    if (label?.nutrientBasisQuantityTypeCode === 'By Package or Container') {
      return 'Container';
    }
    if (label?.nutrientBasisQuantityTypeCode === 'By Measure') {
      return 'Measure';
    }

    return 'Serving';
  };

  return (
    <div class='nutrition-facts scroller'>
      <h1 className='nutrition-facts__label'>Nutrition Facts</h1>
      <SeparatorBar />
      <div className='nutrition-facts__servingsize'>
        {label?.numberOfServingsPerPackage != null ? (
          <div className='nutrition-facts__servings-per-container'>
            <p style={{ textTransform: 'capitalize', marginBottom: 0 }}>
              {servingPerContainerDescriptor ? (
                <span>{servingPerContainerDescriptor}&nbsp;</span>
              ) : null}
              {label?.numberOfServingsPerPackage} servings per container
            </p>
          </div>
        ) : null}
        <div className='nutrition-facts__servingsize-bold'>
          <div>Serving size</div>
          <div class='nutrition-facts__servingsize-word-break'>
            {label?.servingSizeDescription}{' '}
            {label?.servingSize &&
              `(${label?.servingSize} ${getUomTypesAbbreviations(
                basicUomProperties,
                label?.servingSizeMeasurement,
              )})`}
          </div>
        </div>
      </div>
      <SeparatorBar height={7} />
      <div className='nutrition-facts__calories-container'>
        <div>
          <div className='nutrition-facts__calories'>
            Amount Per {getNutrientBasisQtyTypeCode(label)}
          </div>
          <div className='nutrition-facts__calories_new-calories'>Calories</div>
        </div>

        <div className='nutrition-facts__calories_numeric'>
          {label?.calories}
        </div>
      </div>
      <SeparatorBar height={3} />
      <div className='nutrition-facts__dv-heading'>% Daily Value*</div>
      {primaryNutrition?.totalFat && (
        <NutrientRow
          label='Total Fat'
          nutrient={primaryNutrition?.totalFat}
          measurementPrecisionCode={
            primaryNutrition?.totalFat?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.totalFat?.dailyValuePrecisionCode
          }
          labelBold={true}
          percentBold={true}
          percent={parseNumber(
            primaryNutrition?.totalFat?.dailyValueIntakePercent,
          )}
        />
      )}
      {primaryNutrition?.saturatedFat && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            primaryNutrition?.saturatedFat?.nutritionTypeCode,
          )}
          nutrient={primaryNutrition?.saturatedFat}
          indent={10}
          percentBold={true}
          percent={parseNumber(
            primaryNutrition?.saturatedFat?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            primaryNutrition?.saturatedFat?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.saturatedFat?.dailyValuePrecisionCode
          }
        />
      )}
      {primaryNutrition?.transFat && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            primaryNutrition?.transFat?.nutritionTypeCode,
          )}
          nutrient={primaryNutrition?.transFat}
          indent={10}
          percent={parseNumber(
            primaryNutrition?.transFat?.dailyValueIntakePercent,
          )}
          labelItalic={true}
          measurementPrecisionCode={
            primaryNutrition?.transFat?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.transFat?.dailyValuePrecisionCode
          }
        />
      )}
      {primaryNutrition?.polyunsaturatedFat && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            primaryNutrition?.polyunsaturatedFat?.nutritionTypeCode,
          )}
          nutrient={primaryNutrition?.polyunsaturatedFat}
          percent={parseNumber(
            primaryNutrition?.polyunsaturatedFat?.dailyValueIntakePercent,
          )}
          indent={10}
          measurementPrecisionCode={
            primaryNutrition?.polyunsaturatedFat?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.polyunsaturatedFat?.dailyValuePrecisionCode
          }
        />
      )}
      {primaryNutrition?.monounsaturatedFat && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            primaryNutrition?.monounsaturatedFat?.nutritionTypeCode,
          )}
          nutrient={primaryNutrition?.monounsaturatedFat}
          percent={parseNumber(
            primaryNutrition?.monounsaturatedFat?.dailyValueIntakePercent,
          )}
          indent={10}
          measurementPrecisionCode={
            primaryNutrition?.monounsaturatedFat?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.monounsaturatedFat?.dailyValuePrecisionCode
          }
        />
      )}
      {primaryNutrition?.cholesterol && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            primaryNutrition?.cholesterol?.nutritionTypeCode,
          )}
          nutrient={primaryNutrition?.cholesterol}
          labelBold={true}
          percentBold={true}
          percent={parseNumber(
            primaryNutrition?.cholesterol?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            primaryNutrition?.cholesterol?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.cholesterol?.dailyValuePrecisionCode
          }
        />
      )}
      {primaryNutrition?.sodium && (
        <NutrientRow
          label='Sodium'
          nutrient={primaryNutrition?.sodium}
          labelBold={true}
          percentBold={true}
          percent={parseNumber(
            primaryNutrition?.sodium?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            primaryNutrition?.sodium?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.sodium?.dailyValuePrecisionCode
          }
        />
      )}
      {primaryNutrition?.fluoride && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            primaryNutrition?.fluoride?.nutritionTypeCode,
          )}
          nutrient={primaryNutrition?.fluoride}
          labelBold={true}
          percentBold={true}
          percent={parseNumber(
            primaryNutrition?.fluoride?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            primaryNutrition?.fluoride?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.fluoride?.dailyValuePrecisionCode
          }
        />
      )}
      {primaryNutrition?.totalCarbohydrate && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            primaryNutrition?.totalCarbohydrate?.nutritionTypeCode,
          )}
          nutrient={primaryNutrition?.totalCarbohydrate}
          labelBold={true}
          percentBold={true}
          percent={parseNumber(
            primaryNutrition?.totalCarbohydrate?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            primaryNutrition?.totalCarbohydrate?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.totalCarbohydrate?.dailyValuePrecisionCode
          }
        />
      )}
      {primaryNutrition?.xylitol && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            primaryNutrition?.xylitol?.nutritionTypeCode,
          )}
          nutrient={primaryNutrition?.xylitol}
          indent={10}
          percent={parseNumber(
            primaryNutrition?.xylitol?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            primaryNutrition?.xylitol?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.xylitol?.dailyValuePrecisionCode
          }
        />
      )}
      <NutrientRow
        label={getNutritionNameByTypeCode(
          primaryNutrition?.dietaryFiber?.nutritionTypeCode,
        )}
        nutrient={primaryNutrition?.dietaryFiber}
        indent={10}
        percentBold={true}
        percent={parseNumber(
          primaryNutrition?.dietaryFiber?.dailyValueIntakePercent,
        )}
        measurementPrecisionCode={
          primaryNutrition?.dietaryFiber?.measurementPrecisionCode
        }
        dailyValuePrecisionCode={
          primaryNutrition?.dietaryFiber?.dailyValuePrecisionCode
        }
      />
      {primaryNutrition?.fibreWaterSoluble && (
        <NutrientRow
          label='Soluble Fiber'
          nutrient={primaryNutrition?.fibreWaterSoluble}
          indent={15}
          indentBar={15}
          percent={parseNumber(
            primaryNutrition?.fibreWaterSoluble?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            primaryNutrition?.fibreWaterSoluble?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.fibreWaterSoluble?.dailyValuePrecisionCode
          }
        />
      )}
      {primaryNutrition?.fibreWaterInsoluble && (
        <NutrientRow
          label='Insoluble Fiber'
          nutrient={primaryNutrition?.fibreWaterInsoluble}
          indent={15}
          indentBar={15}
          percent={parseNumber(
            primaryNutrition?.fibreWaterInsoluble?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            primaryNutrition?.fibreWaterInsoluble?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.fibreWaterInsoluble?.dailyValuePrecisionCode
          }
        />
      )}
      {primaryNutrition?.totalSugars && (
        <NutrientRow
          label={'Total Sugars'}
          nutrient={primaryNutrition?.totalSugars}
          indent={10}
          measurementPrecisionCode={
            primaryNutrition?.totalSugars?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.totalSugars?.dailyValuePrecisionCode
          }
        />
      )}
      {primaryNutrition?.addedSugars && (
        <NutrientRow
          label={`Added Sugars  ${
            primaryNutrition?.addedSugars?.quantity >= 0
              ? parseNumber(primaryNutrition?.addedSugars?.quantity)
              : ''
          }${showAbbr(primaryNutrition?.addedSugars?.quantityUom, 'g')}`}
          indent={15}
          indentBar={15}
          percentBold={true}
          percent={parseNumber(
            primaryNutrition?.addedSugars?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            primaryNutrition?.addedSugars?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.addedSugars?.dailyValuePrecisionCode
          }
        />
      )}
      {primaryNutrition?.sugarAlcohol && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            primaryNutrition?.sugarAlcohol?.nutritionTypeCode,
          )}
          nutrient={primaryNutrition?.sugarAlcohol}
          indent={10}
          percent={parseNumber(
            primaryNutrition?.sugarAlcohol?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            primaryNutrition?.sugarAlcohol?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.sugarAlcohol?.dailyValuePrecisionCode
          }
        />
      )}
      {primaryNutrition?.sorbitol && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            primaryNutrition?.sorbitol?.nutritionTypeCode,
          )}
          nutrient={primaryNutrition?.sorbitol}
          indent={10}
          percent={parseNumber(
            primaryNutrition?.sorbitol?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            primaryNutrition?.sorbitol?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.sorbitol?.dailyValuePrecisionCode
          }
        />
      )}
      {primaryNutrition?.maltitol && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            primaryNutrition?.maltitol?.nutritionTypeCode,
          )}
          nutrient={primaryNutrition?.maltitol}
          indent={10}
          percent={parseNumber(
            primaryNutrition?.maltitol?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            primaryNutrition?.maltitol?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.maltitol?.dailyValuePrecisionCode
          }
        />
      )}
      {primaryNutrition?.erythritol && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            primaryNutrition?.erythritol?.nutritionTypeCode,
          )}
          nutrient={primaryNutrition?.erythritol}
          percent={parseNumber(
            primaryNutrition?.erythritol?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            primaryNutrition?.erythritol?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.erythritol?.dailyValuePrecisionCode
          }
        />
      )}
      {primaryNutrition?.protein && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            primaryNutrition?.protein?.nutritionTypeCode,
          )}
          nutrient={primaryNutrition?.protein}
          labelBold={true}
          percentBold={true}
          percent={parseNumber(
            primaryNutrition?.protein?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            primaryNutrition?.protein?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            primaryNutrition?.protein?.dailyValuePrecisionCode
          }
        />
      )}
      <SeparatorBar height={7} />
      {secondaryNutrition?.iodide && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.iodide?.nutritionTypeCode,
          )}
          nutrient={primaryNutrition?.iodide}
          percent={parseNumber(
            secondaryNutrition?.iodide?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.iodide?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.iodide?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.vitaminD && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.vitaminD?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.vitaminD}
          percent={parseNumber(
            secondaryNutrition?.vitaminD?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.vitaminD?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.vitaminD?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.calcium && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.calcium?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.calcium}
          percent={parseNumber(
            secondaryNutrition?.calcium?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.calcium?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.calcium?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.iron && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.iron?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.iron}
          percent={parseNumber(
            secondaryNutrition?.iron?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.iron?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.iron?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.potassium && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.potassium?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.potassium}
          percent={parseNumber(
            secondaryNutrition?.potassium?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.potassium?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.potassium?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.vitaminA && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.vitaminA?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.vitaminA}
          percent={parseNumber(
            secondaryNutrition?.vitaminA?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.vitaminA?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.vitaminA?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.VitaminC && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.VitaminC?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.VitaminC}
          percent={parseNumber(
            secondaryNutrition?.VitaminC?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.VitaminC?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.VitaminC?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.VitaminE && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.VitaminE?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.VitaminE}
          percent={parseNumber(
            secondaryNutrition?.VitaminE?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.VitaminE?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.VitaminE?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.VitaminD3 && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.VitaminD3?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.VitaminD3}
          percent={parseNumber(
            secondaryNutrition?.VitaminD3?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.VitaminD3?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.VitaminD3?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.Chloride && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.Chloride?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.Chloride}
          percent={parseNumber(
            secondaryNutrition?.Chloride?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.Chloride?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.Chloride?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.Taurine && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.Taurine?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.Taurine}
          percent={parseNumber(
            secondaryNutrition?.Taurine?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.Taurine?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.Taurine?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.Omega6Acids && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.Omega6Acids?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.Omega6Acids}
          percent={parseNumber(
            secondaryNutrition?.Omega6Acids?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.Omega6Acids?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.Omega6Acids?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.Omega3Acids && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.Omega3Acids?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.Omega3Acids}
          percent={parseNumber(
            secondaryNutrition?.Omega3Acids?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.Omega3Acids?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.Omega3Acids?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.Iodine && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.Iodine?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.Iodine}
          percent={parseNumber(
            secondaryNutrition?.Iodine?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.Iodine?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.Iodine?.dailyValuePrecisionCode
          }
        />
      )}

      {secondaryNutrition?.Dietaryfolateequivalentofsyntheticfolicacid && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.Dietaryfolateequivalentofsyntheticfolicacid
              ?.nutritionTypeCode,
          )}
          nutrient={
            secondaryNutrition?.Dietaryfolateequivalentofsyntheticfolicacid
          }
          percent={parseNumber(
            secondaryNutrition?.Dietaryfolateequivalentofsyntheticfolicacid
              ?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.Dietaryfolateequivalentofsyntheticfolicacid
              ?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.Dietaryfolateequivalentofsyntheticfolicacid
              ?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.TotalAminoAcids && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.TotalAminoAcids?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.TotalAminoAcids}
          percent={parseNumber(
            secondaryNutrition?.TotalAminoAcids?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.TotalAminoAcids?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.TotalAminoAcids?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.EssentialAminoAcids && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.EssentialAminoAcids?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.EssentialAminoAcids}
          percent={parseNumber(
            secondaryNutrition?.EssentialAminoAcids?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.EssentialAminoAcids?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.EssentialAminoAcids?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.Molybdenum && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.Molybdenum?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.Molybdenum}
          percent={parseNumber(
            secondaryNutrition?.Molybdenum?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.Molybdenum?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.Molybdenum?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.Choline && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.Choline?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.Choline}
          percent={parseNumber(
            secondaryNutrition?.Choline?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.Choline?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.Choline?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.VitaminB6 && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.VitaminB6?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.VitaminB6}
          percent={parseNumber(
            secondaryNutrition?.VitaminB6?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.VitaminB6?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.VitaminB6?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.VitaminBcomplex && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.VitaminBcomplex?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.VitaminBcomplex}
          percent={parseNumber(
            secondaryNutrition?.VitaminBcomplex?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.VitaminBcomplex?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.VitaminBcomplex?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.VitaminK && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.VitaminK?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.VitaminK}
          percent={parseNumber(
            secondaryNutrition?.VitaminK?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.VitaminK?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.VitaminK?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.thiamin && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.thiamin?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.thiamin}
          percent={parseNumber(
            secondaryNutrition?.thiamin?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.thiamin?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.thiamin?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.riboflavin && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.riboflavin?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.riboflavin}
          percent={parseNumber(
            secondaryNutrition?.riboflavin?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.riboflavin?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.riboflavin?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.niacin && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.niacin?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.niacin}
          percent={parseNumber(
            secondaryNutrition?.niacin?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.niacin?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.niacin?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.vitaminB6 && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.vitaminB6?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.vitaminB6}
          percent={parseNumber(
            secondaryNutrition?.vitaminB6?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.vitaminB6?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.vitaminB6?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.folicAcid && (
        <NutrientRow
          label={'Synthetic Folic Acid'}
          nutrient={secondaryNutrition?.folicAcid}
          percent={parseNumber(
            secondaryNutrition?.folicAcid?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.folicAcid?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.folicAcid?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.folate && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.folate?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.folate}
          percent={parseNumber(
            secondaryNutrition?.folate?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.folate?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.folate?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.vitaminB12 && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.vitaminB12?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.vitaminB12}
          percent={parseNumber(
            secondaryNutrition?.vitaminB12?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.vitaminB12?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.vitaminB12?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.biotin && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.biotin?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.biotin}
          percent={parseNumber(
            secondaryNutrition?.biotin?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.biotin?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.biotin?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.pantothenicAcid && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.pantothenicAcid?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.pantothenicAcid}
          percent={parseNumber(
            secondaryNutrition?.pantothenicAcid?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.pantothenicAcid?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.pantothenicAcid?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.phosphorus && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.phosphorus?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.phosphorus}
          percent={parseNumber(
            secondaryNutrition?.phosphorus?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.phosphorus?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.phosphorus?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.magnesium && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.magnesium?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.magnesium}
          percent={parseNumber(
            secondaryNutrition?.magnesium?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.magnesium?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.magnesium?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.zinc && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.zinc?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.zinc}
          percent={parseNumber(
            secondaryNutrition?.zinc?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.zinc?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.zinc?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.selenium && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.selenium?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.selenium}
          percent={parseNumber(
            secondaryNutrition?.selenium?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.selenium?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.selenium?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.copper && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.copper?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.copper}
          percent={parseNumber(
            secondaryNutrition?.copper?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.copper?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.copper?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.manganese && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.manganese?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.manganese}
          percent={parseNumber(
            secondaryNutrition?.manganese?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.manganese?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.manganese?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.chromium && (
        <NutrientRow
          label={getNutritionNameByTypeCode(
            secondaryNutrition?.chromium?.nutritionTypeCode,
          )}
          nutrient={secondaryNutrition?.chromium}
          percent={parseNumber(
            secondaryNutrition?.chromium?.dailyValueIntakePercent,
          )}
          measurementPrecisionCode={
            secondaryNutrition?.chromium?.measurementPrecisionCode
          }
          dailyValuePrecisionCode={
            secondaryNutrition?.chromium?.dailyValuePrecisionCode
          }
        />
      )}
      {secondaryNutrition?.caffeine && (
        <>
          <SeparatorBar height={3} />
          <NutrientRow
            label={getNutritionNameByTypeCode(
              secondaryNutrition?.caffeine?.nutritionTypeCode,
            )}
            nutrient={secondaryNutrition?.caffeine}
            percent={parseNumber(
              secondaryNutrition?.caffeine?.dailyValueIntakePercent,
            )}
            measurementPrecisionCode={
              secondaryNutrition?.caffeine?.measurementPrecisionCode
            }
            dailyValuePrecisionCode={
              secondaryNutrition?.caffeine?.dailyValuePrecisionCode
            }
          />
        </>
      )}
      {label?.ingredientNotice && (
        <>
          <SeparatorBar height={3} />
          <div className='nutrition-facts__ingredient-notice'>
            <p>{label?.ingredientNotice}</p>
          </div>
        </>
      )}
      <SeparatorBar height={3} />
      <div className='nutrition-facts__footnote'>
        {!!label?.fdaLabelFootNote && (
          <p>
            <span>* {label?.fdaLabelFootNote}</span>
          </p>
        )}
      </div>
    </div>
  );
}

export default VerticalLabel;
